import { useEffect, useRef, useState } from 'react';
import s from './ImageEditorPage.module.css';
import { useSelector } from 'react-redux';
import { selectIsLoadingScript } from '../../store/selectors/projectSelectors';
import ChoosePromptPanel from '../text-editor-page/widgets/choose-prompt-panel/ChoosePromptPanel';
import PromptImageEditToolbar from './widgets/prompt-image-edit-toolbar/PromptImageEditToolbar';
import TopToolbar from './widgets/top-toolbar/TopToolbar';
import Canvas from './widgets/canvas/Canvas';
import CurrentImages from './widgets/current-images/CurrentImages';
import Loader from '../../shared/ui/loader/Loader';

import { useAppDispatch } from '../../store/store';
import ToolsContainer from '../text-editor-page/widgets/tools-container/ToolsContainer';
import { CanvasContainerContext } from '../../shared/context/CanvasContainerContext';
import Button from '../../shared/ui/button/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { toggleActivePrompt } from '../../store/slices/pageSlice';
import ZoomSlider from './widgets/zoom-slider/ZoomSlider';
import { selectActivePrompt } from '../../store/selectors/pageSelectors';
import { selectUser, selectUserProfile } from '../../store/selectors/authSelectors';
import FullScreenModal from './widgets/full-screen-modal/FullScreenModal';
import {
    selectImageProject,
    selectImageLayout,
    selectSelectedImage,
    selectLoadingDownload,
    selectHasChanged,
} from '../../store/selectors/imageSelectors';
import { fetchImageProject, updateLayoutImage } from '../../shared/api/imageProjectApi';
import { fetchUserUploads } from '../../shared/api/imageProjectApi';
import { setImageProject, setSelectedImage, setHasChanged } from '../../store/slices/imageSlice';
import ChangeTitle from '../text-editor-page/widgets/change-title/ChangeTitle';
import { uploadCanvasImage } from '../../shared/helpers/downloadCanvas';
import { updateImageProject } from '../../shared/api/imageProjectApi';
import { fetchProject } from '../../shared/api/textProjectApi';
import LogoutButton from '../../shared/ui/logout-button/LogoutButton';
import { setCurrentProject } from '../../store/slices/projectSlice';
import SaveFrameLoader from '../../shared/ui/loader/SaveFrameLoader';
import { handleSaveImage } from '../../shared/utils/handleSaveImage';
import { useDebouncedSaveImage } from '../../shared/hooks/useDebouncedSaveImage';
import { useDebouncedSaveLayout } from '../../shared/hooks/useDebounsedSaveLayout';
import { setShouldBlock } from '../../store/slices/navigationGuardSlice';
import { navigationHandlerRef } from '../../store/ref/navigationHandlerRef';

const ImageEditorPage = () => {
    const isLoadingScript = useSelector(selectIsLoadingScript);
    const activePrompt = useSelector(selectActivePrompt);
    const imageProject = useSelector(selectImageProject);
    const user = useSelector(selectUser);
    const hasChanged = useSelector(selectHasChanged);
    const imageLayout = useSelector(selectImageLayout);
    const selectedImage = useSelector(selectSelectedImage);
    const loadingDownload = useSelector(selectLoadingDownload);

    const dispatch = useAppDispatch();

    const containerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [newImageWithEffects, setNewImageWithEffects] = useState<File | null>(null);
    const [isLoadingSaveFrame, setIsLoadingSaveFrame] = useState(false);

    useDebouncedSaveImage({
        activePrompt,
        selectedImage,
        dispatch,
        newImageWithEffects,
    });
    useDebouncedSaveLayout({
        imageLayout,
        dispatch,
        imageProjectId: imageProject?.id ?? '',
    });
    const { projectId } = useParams();

    useEffect(() => {
        if (projectId) {
            const fetchData = async () => {
                const project = await dispatch(fetchImageProject(projectId)).unwrap();

                const idFromResponse = project.projectId;
                dispatch(fetchProject({ projectId: idFromResponse }));
            };
            fetchData();
        }
    }, [dispatch, projectId]);

    useEffect(() => {
        if (
            imageLayout &&
            projectId &&
            imageProject &&
            imageProject.allImagesForLayouts.length === 1
        ) {
            const imageData = imageProject?.allImagesForLayouts[0];

            if (imageData) {
                dispatch(setSelectedImage({ image: imageData, layoutId: imageLayout.id }));
            }
        }
    }, [dispatch, imageProject]);

    useEffect(() => {
        if (user) {
            dispatch(fetchUserUploads(user.id));
        }
    }, [dispatch, user]);

    const handleBackToContent = async () => {
        if (hasChanged) {
            setIsLoadingSaveFrame(true);
            dispatch(setHasChanged(false));
            await handleSaveImage({
                activePrompt,
                selectedImage,
                dispatch,
                newImageWithEffects,
            });
            if (containerRef.current && imageLayout?.id) {
                const canvasEl = containerRef.current.querySelector('canvas') as HTMLCanvasElement;
                if (canvasEl) {
                    const file = await uploadCanvasImage(containerRef);
                    if (file) {
                        await dispatch(updateLayoutImage({ layoutId: imageLayout.id, file }));
                    }
                }
            }
            setIsLoadingSaveFrame(false);
        }
        navigate(`/text_project/${imageProject?.projectId}`);
        dispatch(toggleActivePrompt(null));
    };

    useEffect(() => {
        return () => {
            dispatch(setImageProject(null));
            dispatch(toggleActivePrompt(null));
            dispatch(setSelectedImage({ image: null, layoutId: null }));
            dispatch(setCurrentProject(null));
        };
    }, []);
    const handleUpdateTitle = async (value: string) => {
        if (imageProject?.id) {
            await dispatch(updateImageProject({ imageProjectId: imageProject?.id, title: value }));
        }
    };
    useEffect(() => {
        console.log('hasChanged:', hasChanged);
        if (!hasChanged && navigationHandlerRef?.current) return;

        dispatch(setShouldBlock(true));

        navigationHandlerRef.current = async () => {
            setIsLoadingSaveFrame(true);
            dispatch(setHasChanged(false));
            await handleSaveImage({
                activePrompt,
                selectedImage,
                dispatch,
                newImageWithEffects,
            });
            if (containerRef.current && imageLayout?.id) {
                const file = await uploadCanvasImage(containerRef);
                if (file) {
                    await dispatch(updateLayoutImage({ layoutId: imageLayout.id, file }));
                }
            }
            setIsLoadingSaveFrame(false);
        };
    }, [hasChanged]);

    return imageProject !== null ? (
        <CanvasContainerContext.Provider value={containerRef}>
            <div className={s.content}>
                <div className={s.left_column}>
                    <div className={s.top_bar}>
                        <Button
                            text={'Back to Content'}
                            onClick={handleBackToContent}
                            style={{
                                width: '130px',
                                height: '26px',
                                fontSize: '12px',
                                backgroundColor: '#FFDD35',
                            }}
                        />
                        <ChangeTitle
                            initialTitle={imageProject?.title}
                            updateText={handleUpdateTitle}
                        />
                    </div>
                    <ChoosePromptPanel type={'image'} />
                    <PromptImageEditToolbar />
                </div>
                <div className={s.center_column}>
                    <div className={s.top_toolbar}>
                        <TopToolbar />
                    </div>

                    <Canvas ref={containerRef} setNewImageWithEffects={setNewImageWithEffects} />

                    <div className={s.current_images_container}>
                        <CurrentImages />
                        {selectedImage && <ZoomSlider />}
                    </div>
                </div>
                <div className={s.right_column}>
                    <ToolsContainer
                        isImageFocused={true}
                        pageType={'image'}
                        newImageWithEffects={newImageWithEffects}
                    />
                </div>

                {activePrompt === 'fullScreen' && <FullScreenModal />}
                <div className={s.logout_button}>
                    <LogoutButton />
                </div>
            </div>

            {(isLoadingScript || loadingDownload) && <Loader />}
            {isLoadingSaveFrame && <SaveFrameLoader />}
        </CanvasContainerContext.Provider>
    ) : (
        <Loader />
    );
};

export default ImageEditorPage;
